import ActionButton from 'components/ActionButton';
import React, { useEffect, useState } from 'react';
import styles from './index.module.scss';
import * as yup from 'yup';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers';
import { Error } from 'components/message/Error';
import { STRIPE_PRODUCT_TYPE, getImageUrlFromName, showError, showSuccess } from 'utils';
import { createStripeProduct, fetchStripeProductBySlug, updateStripeProduct } from 'services';
import ImageUploadComponent from 'components/ImageUploadComponent';
import DropDown from 'components/FormFields/dropdown';
import MDEditor from '@uiw/react-md-editor';
import HowItWorksSteps from 'components/HowItWorksSteps';
import { HexColorInput, HexColorPicker } from 'react-colorful';
import { Loading } from 'components/loading/Loading';
import FaqsForSubscription from 'components/FaqsForSubscription';
import ActionCheckbox from 'components/ActionCheckBox';

const validationSchema = yup.object().shape({
  name: yup.string().required('Please enter name'),
  enName: yup.string(),
  description: yup.string().required('Please enter description'),
  enDescription: yup.string(),
  image: yup.object(),
  heroImage: yup.object(),
  logoImage: yup.object(),
  subscriptionBackgroundImage: yup.object(),
  faviconImage: yup.object(),
});

type FormData = {
  name: string;
  enName: string;
  description: string;
  enDescription: string;
  image: object;
  heroImage: object;
  logoImage: object;
  subscriptionBackgroundImage: object;
  faviconImage: object;
};

const CreateStripeProduct = props => {
  const { slug } = props.match.params;
  const [loading, setLoading] = useState<boolean>(false);
  const [howItWorksSteps, setHowItWorksSteps] = useState<any>([]);
  const [faqs, setFaqs] = useState<any>([]);
  const [data, setData] = useState() as any;
  const [imageForPreview, setImageForPreview] = useState<string>('');
  const [faviconImage, setFaviconImage] = useState<string>('');
  const [logoImage, setLogoImage] = useState<string>('');
  const [subscriptionBackgroundImage, setSubscriptionBackgroundImage] = useState<string>('');
  const [heroImageForPreview, setHeroImageForPreview] = useState<string>('');

  const {
    register,
    handleSubmit,
    errors,
    setValue,
  } = useForm<FormData>({ resolver: yupResolver(validationSchema) });

  const fetchStripeProductBySlugFn = async () => {
    setLoading(true);
    try {
      const response = await fetchStripeProductBySlug(slug);
      const { name, enName, description, enDescription, image, heroImage,
        logoImage, howItWorksSteps, subscriptionBackgroundImage, faviconImage, faqs } = response;
      setData(response);
      setValue('name', name);
      setValue('enName', enName);
      setValue('description', description);
      setValue('enDescription', enDescription);
      setHowItWorksSteps(howItWorksSteps || []);
      setFaqs(faqs || []);
      setLogoImage(logoImage?.name ? getImageUrlFromName(logoImage.name) : '');
      setFaviconImage(faviconImage?.name ? getImageUrlFromName(faviconImage?.name) : '');
      setSubscriptionBackgroundImage(subscriptionBackgroundImage?.name ? getImageUrlFromName(subscriptionBackgroundImage?.name) : '');
      setImageForPreview(image?.name ? getImageUrlFromName(image?.name) : '');
      setHeroImageForPreview(heroImage?.name ? getImageUrlFromName(heroImage?.name) : '');
    } catch (e) {
      showError(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    register('image');
    register('heroImage');
    register('logoImage');
    register('faviconImage');
    register('subscriptionBackgroundImage');
    if (slug) {
      fetchStripeProductBySlugFn();
    }
  }, []);

  const saveStripeProductFn = handleSubmit(async (values) => {
    setLoading(true);
    try {
      if (!data?.productType) {
        throw 'Please select product type.';
      }
      const dataForStripeProduct: any = {
        ...values, productType: data.productType, heroText: data.heroText, howItWorksSteps,
        howItWorksDescription: data.howItWorksDescription, howItWorksTitle: data.howItWorksTitle,
        successText: data.successText, subscriptionFormHeader: data.subscriptionFormHeader,
        brandColor: data.brandColor, faqs, allowRepeatItem: data?.allowRepeatItem,
      };
      if (slug) {
        dataForStripeProduct.id = data?.objectId;
        const res = await updateStripeProduct(dataForStripeProduct);
        showSuccess('Stripe Product updated successfully.');
        props.history.replace(`/stripe-products/${res?.slug}`);
      } else {
        await createStripeProduct(dataForStripeProduct);
        showSuccess('Stripe Product created successfully.');
        props.history.goBack();
      }
    } catch (e) {
      showError(e);
    } finally {
      setLoading(false);
    }
  });

  const getHeader = () => {
    const heading = slug ? 'Update Stripe Product' : 'Create a New Stripe Product';
    return (
      <div className={styles['header']}>
        <div className={styles['heading']}>{heading}</div>
      </div>
    );
  };

  const getFooter = () => {
    return (
      <div className={styles['footer']}>
        <ActionButton
          text={'Cancel'}
          active={!loading}
          isLoading={false}
          onClick={() => props.history.goBack()}
          customClass={styles['cancel']}
        />
        <ActionButton
          text={'Save'}
          active={!loading}
          isLoading={loading}
          onClick={saveStripeProductFn}
        />
      </div>
    );
  };

  const getFieldError = (error) => {
    if (!error) return null;
    return (
      <Error message={error} />
    );
  };

  const getName = () => {
    return (
      <div className={styles['name']}>
        <div className={styles['label']}>Name*</div>
        <input
          name={'name'}
          defaultValue={data?.name}
          ref={register}
          className={'form-control'}
          disabled={loading}
        />
        {getFieldError(errors?.name?.message)}
      </div>
    );
  };

  const getDescription = () => {
    return (
      <div className={styles['description']}>
        <div className={styles['label']}>Description*</div>
        <textarea
          defaultValue={data?.description}
          name={'description'}
          ref={register}
          className={'form-control'}
          disabled={loading}
        />
        {getFieldError(errors?.description?.message)}
      </div>
    );
  };

  const getEnName = () => {
    return (
      <div className={styles['name']}>
        <div className={styles['label']}>English Name</div>
        <input
          name={'enName'}
          defaultValue={data?.enName}
          ref={register}
          className={'form-control'}
          disabled={loading}
        />
        {getFieldError(errors?.enName?.message)}
      </div>
    );
  };

  const getEnDescription = () => {
    return (
      <div className={styles['description']}>
        <div className={styles['label']}>English Description</div>
        <textarea
          name={'enDescription'}
          defaultValue={data?.enDescription}
          ref={register}
          className={'form-control'}
          disabled={loading}
        />
        {getFieldError(errors?.enDescription?.message)}
      </div>
    );
  };

  const getAllowRepeattItemInput=()=>{
    return (
      <div className='mb-3'>
        {/* <div className={styles['active-input-container']}>
          <ActionCheckbox
            checked={data?.allowRepeatItem}
            disabled={loading}
            onChange={e => setData((prevData) => ({ ...prevData, allowRepeatItem: e }))}
          />            
          <div className={styles['active-label']}>Allow Repeat Item</div>
        </div> */}
        <div className={styles['active-input-container']}>
          <input
            type={'checkbox'}
            className={'form-control'}
            checked={data?.allowRepeatItem}
            onChange={e => setData((prevData) => ({ ...prevData, allowRepeatItem: !prevData?.allowRepeatItem }))}
          />
          <div className={styles['active-label']} >Allow Repeat Item</div>
        </div>
      </div>
    );
  };

  const getType = () => {
    return (
      <div className={styles['description']}>
        <DropDown
          title="Product Type"
          onChange={productType =>
            setData((prevData) => ({ ...prevData, productType }))}
          options={STRIPE_PRODUCT_TYPE}
          value={data?.productType} />
      </div>
    );
  };

  const getSuccessText = () => {
    return (
      <div className={styles['name'] + ' mb-3 mt-4'}>
        <div className={styles['label']}>Success Text</div>
        <div data-color-mode='light'>
          <MDEditor
            value={data?.successText}
            onChange={e => setData((prevData) => ({ ...prevData, successText: e }))}
            style={{ color: 'black' }}
          />
        </div>
      </div>
    );
  };


  const getSubscriptionFormHeaderText = () => {
    return (
      <div className={styles['name'] + ' mb-3 mt-4'}>
        <div className={styles['label']}>Subscription Form Header</div>
        <div data-color-mode='light'>
          <MDEditor
            value={data?.subscriptionFormHeader}
            onChange={e => setData((prevData) => ({ ...prevData, subscriptionFormHeader: e }))}
            style={{ color: 'black' }}
          />
        </div>
      </div>
    );
  };

  const getHowItWorksTitle = () => {
    return (
      <div className={styles['name'] + ' mb-3'}>
        <div className={styles['label']}>Main Title: </div>
        <div data-color-mode='light'>
          <MDEditor
            value={data?.howItWorksTitle}
            onChange={(e) => setData((prevData) => ({ ...prevData, howItWorksTitle: e }))}
            style={{ color: 'black' }}
          />
        </div>
      </div>
    );
  };

  const getHowItWorksDescription = () => {
    return (
      <div className={styles['name']}>
        <div className={styles['label']}>Main Description: </div>
        <div data-color-mode='light'>
          <MDEditor
            value={data?.howItWorksDescription}
            onChange={(e) => setData((prevData) => ({ ...prevData, howItWorksDescription: e }))}
            style={{ color: 'black' }}
          />
        </div>
      </div>
    );
  };

  const getBrandColor = () => {
    return (
      <div className={styles['name']}>
        <div className={styles['label']}>Brand Color</div>
        <HexColorInput color={data?.brandColor} onChange={e => setData((prevData) => ({ ...prevData, brandColor: e }))} prefixed alpha />
        <HexColorPicker color={data?.brandColor} onChange={e => setData((prevData) => ({ ...prevData, brandColor: e }))} />
      </div>
    );
  };

  const onImageUpload = (id, image) => {
    setValue('image', image);
    setImageForPreview(image?._url);
  };

  const onLogoImageUpload = (id, image) => {
    setValue('logoImage', image);
    setImageForPreview(image?._url);
  };

  const onFaviconImageUpload = (id, image) => {
    setValue('faviconImage', image);
    setFaviconImage(image?._url);
  };

  const onSubscriptionBackgroundImageUpload = (id, image) => {
    setValue('subscriptionBackgroundImage', image);
    setSubscriptionBackgroundImage(image?._url);
  };


  const onHeroImageUpload = (id, image) => {
    setValue('heroImage', image);
    setHeroImageForPreview(image?._url);
  };

  const getImageInput = () => {
    return (
      <div className={styles['image-container']}>
        <div className={styles['label'] + ' mb-2 ml-2'}>Product Image</div>
        <ImageUploadComponent
          src={imageForPreview?.toString()}
          uploadAPI={onImageUpload}
          onFileSelect={setImageForPreview}
          forceShowUploadButton
        />
      </div>
    );
  };

  const getFaviconImageInput = () => {
    return (
      <div className={styles['image-container'] + ' mb-4'}>
        <div className={styles['label'] + ' mb-2 ml-2'}>Favicon Image</div>
        <ImageUploadComponent
          src={faviconImage.toString()}
          uploadAPI={onFaviconImageUpload}
          onFileSelect={setFaviconImage}
          forceShowUploadButton
        />
      </div>
    );
  };

  const getSubscriptionBackgroundImage = () => {
    return (
      <div className={styles['description']}>
        <div className={styles['label'] + ' mb-2 ml-2'}>Subscription Background Image</div>
        <div className={styles['hero-image-container'] + ' mb-5'}>
          <ImageUploadComponent
            src={subscriptionBackgroundImage.toString()}
            uploadAPI={onSubscriptionBackgroundImageUpload}
            onFileSelect={setSubscriptionBackgroundImage}
            forceShowUploadButton
          />
        </div>
      </div>
    );
  };

  const getHeroImageInput = () => {
    return (
      <div className={styles['description']}>
        <div className={styles['label'] + ' mt-2 mb-2 ml-2'}>Hero Image</div>
        <div className={styles['hero-image-container'] + ' mb-5'}>
          <ImageUploadComponent
            src={heroImageForPreview.toString()}
            uploadAPI={onHeroImageUpload}
            onFileSelect={setHeroImageForPreview}
            forceShowUploadButton
          />
        </div>
      </div>
    );
  };

  const getLogoImageInput = () => {
    return (
      <div className={styles['image-container']}>
        <div className={styles['label'] + ' mt-2 mb-2 ml-2'}>Logo</div>
        <ImageUploadComponent
          src={logoImage.toString()}
          uploadAPI={onLogoImageUpload}
          onFileSelect={setLogoImage}
          forceShowUploadButton
        />
      </div>
    );
  };

  const getHeroTextEditor = () => {
    return (
      <>
        <div className={styles['description'] + ' ' + styles['herotext-editor']}>
          <div className={styles['label'] + ' mb-2 ml-2'}>Hero Text</div>
          <div data-color-mode='light'>
            <MDEditor
              value={data?.heroText}
              onChange={(e) => setData((prevData) => ({ ...prevData, heroText: e }))}
              style={{ color: 'black' }}
            />
          </div>
        </div>
      </>
    );
  };

  function getHowItWorks() {
    return (
      <>
        <div className="card mb-3">
          <div className="card-header">How It works?: Basic Details</div>
          <div className="card-body">
            {getHowItWorksTitle()}
            {getHowItWorksDescription()}
          </div>
        </div>
        <>
          <div className={styles['label'] + ' ml-2'}>How it Works? Steps :</div>
          <HowItWorksSteps howItWorksSteps={howItWorksSteps} setHowItWorksSteps={setHowItWorksSteps} isEditor />
        </>
      </>
    );
  }

  function getFAQsSection() {
    return (
      <>
        <div className={styles['label'] + ' ml-2'}>FAQ</div>
        <FaqsForSubscription faqs={faqs} setFaqs={setFaqs} isEditor />
      </>
    );
  }

  function getSubContainerInputs() {
    return (
      <div className={styles['sub-container']}>
        <div className='d-block'>
          {getImageInput()}
          {getFaviconImageInput()}
          {getLogoImageInput()}
        </div>
        <div className={styles['item-details']}>
          {getName()}
          {getDescription()}
          {getEnName()}
          {getEnDescription()}
          {getAllowRepeattItemInput()}
          {getType()}
          {getBrandColor()}
        </div>
      </div>
    );
  }

  if (loading) {
    return <Loading />;
  }
  return (
    <div className={styles['main-container']}>
      {getHeader()}
      {getSubContainerInputs()}
      {getHeroTextEditor()}
      {getHeroImageInput()}
      {getSubscriptionBackgroundImage()}
      {getSuccessText()}
      {getSubscriptionFormHeaderText()}
      {getHowItWorks()}
      {getFAQsSection()}
      {getFooter()}
    </div>
  );
};

export { CreateStripeProduct };
